import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Routes, Route, useLocation, useNavigate, Link } from 'react-router-dom';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import AboutPage from './components/AboutPage';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';

// --- CONFIGURATION ---
const ADSENSE_CLIENT_ID = "ca-pub-3880394864354421"; 
const ADSENSE_SLOT_SIDEBAR = "3021389352"; 
const ADSENSE_SLOT_FOOTER = "2227182431"; 

// --- Google Ad Component ---
interface GoogleAdProps {
  className?: string;
  style?: React.CSSProperties;
  client: string;
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'vertical' | 'horizontal';
  responsive?: string;
  layoutKey?: string;
}

const GoogleAd: React.FC<GoogleAdProps> = ({
  className,
  style,
  client,
  slot,
  format = 'auto',
  responsive = 'true',
  layoutKey
}) => {
  useEffect(() => {
    try {
      const adsbygoogle = (window as any).adsbygoogle || [];
      adsbygoogle.push({});
    } catch (e) {
      console.error("AdSense Error:", e);
    }
  }, []);

  return (
    <div className={className} style={style}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', height: '100%' }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-full-width-responsive={responsive}
        data-ad-layout-key={layoutKey}
      />
    </div>
  );
};

// --- SVG Icons ---

const SearchIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const ChevronDown = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const AlertTriangle = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const CheckCircle = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const Zap = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const ReplaceIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 4c0-1.1.9-2 2-2" />
    <path d="M20 2c1.1 0 2 .9 2 2" />
    <path d="M22 8c0 1.1-.9 2-2 2" />
    <path d="M16 10c-1.1 0-2-.9-2-2" />
    <path d="m3 7 3 3 3-3" />
    <path d="M6 10V5c0-1.7 1.3-3 3-3h1" />
    <rect x="2" y="14" width="8" height="8" rx="2" />
  </svg>
);

const MoonIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

const SunIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const CopyIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

const UploadIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const DownloadIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

// --- New Icons for Expand/Collapse ---
const ExpandAllIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const CollapseAllIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

const JsonFormatterLogoSvg = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2.5" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
    style={{ color: '#F97316' }}
  >
    <path d="M8 4c-1 0-2 1-2 2v3c0 1-.5 2-1.5 2C5 11 6 12 6 13v3c0 1 1 2 2 2" />
    <path d="M16 4c1 0 2 1 2 2v3c0 1 .5 2 1.5 2C19 11 18 12 18 13v3c0 1-1 2-2 2" />

    <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
  </svg>
);

// --- Helper Functions ---

const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); 
};

// --- Helper Components ---

// --- COMPONENT: Editor with Line Numbers ---
interface LineNumberEditorProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  theme: any;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const LineNumberEditor = React.forwardRef<HTMLTextAreaElement, LineNumberEditorProps>(
  ({ value, onChange, theme, placeholder, readOnly, className, onKeyDown }, ref) => {
    const linesRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Sync refs for parent access
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const handleScroll = () => {
      if (linesRef.current && textareaRef.current) {
        linesRef.current.scrollTop = textareaRef.current.scrollTop;
      }
    };

    const lineCount = value.split('\n').length;
    const lines = Array.from({ length: Math.max(lineCount, 1) }, (_, i) => i + 1);

    return (
      <div className={`relative flex w-full h-full border rounded-lg overflow-hidden ${theme.border} ${className}`}>
        {/* Line Numbers Column */}
        <div
          ref={linesRef}
          className={`flex-shrink-0 w-10 md:w-12 text-right pr-2 pt-4 select-none overflow-hidden text-xs font-mono leading-relaxed border-r ${
            theme.border
          } ${theme.label} ${theme.panelBg}`}
          style={{ lineHeight: '1.5rem' }} // Matching textarea leading-6
        >
          {lines.map((line) => (
            <div key={line} className="opacity-50">
              {line}
            </div>
          ))}
        </div>

        {/* Text Area */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onScroll={handleScroll}
          onKeyDown={onKeyDown}
          readOnly={readOnly}
          placeholder={placeholder}
          className={`flex-1 w-full p-4 bg-transparent resize-none focus:outline-none focus:ring-0 font-mono text-sm leading-relaxed ${theme.text} ${theme.editorBg}`}
          spellCheck="false"
          style={{ lineHeight: '1.5rem' }} // Explicit line height for sync
        />
      </div>
    );
  }
);
LineNumberEditor.displayName = "LineNumberEditor";


const HighlightText = ({ text, highlight }: { text: string; highlight: string }) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }
  const escapedHighlight = escapeRegExp(highlight);
  const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <span key={i} className="bg-yellow-500 text-black px-0.5 rounded-sm">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  );
};

// --- JSON Interactive Tree Viewer ---

// Define the trigger type for expand/collapse actions
type ExpandActionType = 'EXPAND_ALL' | 'COLLAPSE_DEFAULT' | null;
interface ExpandTrigger {
  action: ExpandActionType;
  id: number; // timestamp to force effect re-run
}

interface JsonNodeProps {
  nodeKey: string;
  value: unknown;
  level?: number;
  searchTerm: string;
  darkMode: boolean;
  expandTrigger: ExpandTrigger;
}

const JsonNode: React.FC<JsonNodeProps> = ({
  nodeKey,
  value,
  level = 0,
  searchTerm,
  darkMode,
  expandTrigger
}) => {
  const valueType = typeof value;
  const isObject = value !== null && valueType === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isCollapsible = isObject || isArray;
  const isRoot = nodeKey === "(root)";

  // Initialize: default expanding only the top level (level 0) or items deeper if needed?
  // User req: "by default on pasting we show one level parsed json as we show now"
  // Logic: level < 1 is true for root (0), so root is expanded. Children (level 1) are collapsed.
  const [isExpanded, setIsExpanded] = useState(level < 1);

  // Listen for global expand/collapse triggers
  useEffect(() => {
    if (!expandTrigger.action) return;

    if (expandTrigger.action === 'EXPAND_ALL') {
      setIsExpanded(true);
    } else if (expandTrigger.action === 'COLLAPSE_DEFAULT') {
      // Logic: Collapse back to default state.
      // Default state means only root (level 0) is expanded.
      setIsExpanded(level < 1);
    }
  }, [expandTrigger, level]);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  // --- Colors based on Dark Mode ---
  const colorString = darkMode ? 'text-[#ce9178]' : 'text-red-700';
  const colorNumber = darkMode ? 'text-[#b5cea8]' : 'text-green-700';
  const colorBoolean = darkMode ? 'text-[#569cd6]' : 'text-blue-700';
  const colorNull = darkMode ? 'text-[#569cd6]' : 'text-gray-500';
  const colorKey = darkMode ? 'text-[#9cdcfe]' : 'text-gray-900';
  const colorPunctuation = darkMode ? 'text-[#d4d4d4]' : 'text-gray-600';

  if (!isCollapsible) {
    let valueClass = colorNumber;
    if (valueType === 'string') valueClass = colorString;
    if (valueType === 'boolean') valueClass = colorBoolean;
    if (value === null) valueClass = colorNull;

    return (
      <div className={`font-mono text-sm ${!isRoot ? 'ml-4' : ''} leading-relaxed`}>
        {!isRoot && (
          <span className={`${colorKey} cursor-default`}>
             "<HighlightText text={nodeKey} highlight={searchTerm} />":
          </span>
        )}
        <span className={`ml-2 ${valueClass}`}>
            {valueType === 'string' ? '"' : ''}
            <HighlightText text={String(value)} highlight={searchTerm} />
            {valueType === 'string' ? '"' : ''}
        </span>
      </div>
    );
  }

  const entries = isArray
    ? (value as unknown[]).map((val, i) => [i.toString(), val])
    : Object.entries(value as object);
  const bracketOpen = isArray ? '[' : '{';
  const bracketClose = isArray ? ']' : '}';
  const items = entries.length;

  return (
    <div className={`font-mono text-sm ${!isRoot ? 'ml-4' : ''} leading-relaxed`}>
      <div
        className={`flex items-center cursor-pointer rounded px-1 -ml-1 ${darkMode ? 'hover:bg-[#2a2d2e]' : 'hover:bg-gray-100'}`}
        onClick={toggleExpand}
      >
        {isExpanded ? (
          <ChevronDown className={`w-4 h-4 mr-1 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        ) : (
          <ChevronRight className={`w-4 h-4 mr-1 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        )}
        
        {!isRoot && (
          <span className={colorKey}>
             "<HighlightText text={nodeKey} highlight={searchTerm} />":
          </span>
        )}
        
        <span className={!isRoot ? 'ml-2' : ''}>
          <span className={colorPunctuation}>{bracketOpen}</span>
          {!isExpanded && (
            <span className={`ml-1 italic cursor-default ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {items} {items === 1 ? 'item' : 'items'}
              ...
            </span>
          )}
          {!isExpanded && <span className={colorPunctuation}>{bracketClose}</span>}
        </span>
      </div>

      {isExpanded && (
        <div className={`ml-4 border-l pl-2 ${darkMode ? 'border-[#404040]' : 'border-gray-300'}`}>
          {entries.map(([key, val]) => (
            <JsonNode 
              key={String(key)} 
              nodeKey={String(key)} 
              value={val} 
              level={level + 1} 
              searchTerm={searchTerm} 
              darkMode={darkMode}
              expandTrigger={expandTrigger}
            />
          ))}
          <span className={colorPunctuation}>{bracketClose}</span>
        </div>
      )}
    </div>
  );
};

// --- Debounce Hook ---
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

// --- Main App Component ---

interface JsonFormatterViewProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
}

function JsonFormatterView({ darkMode, setDarkMode }: JsonFormatterViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Navigation View State: 'formatter' | 'minifier'
  const [view, setView] = useState<'formatter' | 'minifier'>(() => 
    location.pathname === '/minifier' ? 'minifier' : 'formatter'
  );

  // Initialize input from localStorage
  const [input, setInput] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('jsonfmt_input_cache') || '';
    }
    return '';
  });

  const debouncedInput = useDebounce(input, 500);

  // Persist input to localStorage when debounced value changes
  useEffect(() => {
    try {
      localStorage.setItem('jsonfmt_input_cache', debouncedInput);
    } catch (e) {
      console.warn('Failed to save to localStorage (likely quota exceeded)', e);
    }
  }, [debouncedInput]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });


  const [copied, setCopied] = useState(false);
  
  // Search State
  const [showSearch, setShowSearch] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for Expand/Collapse All Actions
  const [expandTrigger, setExpandTrigger] = useState<ExpandTrigger>({ action: null, id: 0 });

  // Handle URL changes
  useEffect(() => {
    setView(location.pathname === '/minifier' ? 'minifier' : 'formatter');
  }, [location.pathname]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setView(path === '/minifier' ? 'minifier' : 'formatter');
  };

  // Parse JSON
  const { jsonData, error } = useMemo(() => {
    if (!debouncedInput.trim()) {
      return { jsonData: null, error: null };
    }
    try {
      const parsed = JSON.parse(debouncedInput);
      return { jsonData: parsed, error: null };
    } catch (err) {
      return {
        jsonData: null,
        error: err instanceof Error ? err.message : 'Invalid JSON',
      };
    }
  }, [debouncedInput]);

  // Minify JSON Logic
  const minifiedJson = useMemo(() => {
    if (!jsonData) return '';
    return JSON.stringify(jsonData);
  }, [jsonData]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setInput(event.target.result as string);
      }
    };
    reader.readAsText(file);
    // Reset input value so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleClear = () => {
    if (input.trim() === '') return;
    setInput('');
  };

  const handleDownload = () => {
    if (!jsonData) return;
    
    const content = view === 'minifier' 
      ? JSON.stringify(jsonData) 
      : JSON.stringify(jsonData, null, 2);
    
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'result.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyMinified = async () => {
    if (!minifiedJson) return;
    try {
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = minifiedJson;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();
      document.execCommand("copy");
      document.body.removeChild(tempTextArea);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy!', err);
    }
  };

  // --- Handlers for Expand/Collapse All ---
  const handleExpandAll = () => {
    setExpandTrigger({ action: 'EXPAND_ALL', id: Date.now() });
  };

  const handleCollapseAll = () => {
    setExpandTrigger({ action: 'COLLAPSE_DEFAULT', id: Date.now() });
  };


  // --- Keyboard Shortcuts ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        if (!showSearch) {
            setShowSearch(true);
            setTimeout(() => document.getElementById('search-input')?.focus(), 50);
        } else {
             document.getElementById('search-input')?.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  // --- Search Functions ---
  
  const findNext = useCallback(() => {
    if (!searchText || !textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const currentPos = textarea.selectionEnd;
    const text = textarea.value;
    
    const escapedTerm = escapeRegExp(searchText);
    const regex = new RegExp(escapedTerm, 'gi');
    
    regex.lastIndex = currentPos;
    let match = regex.exec(text);

    if (!match) {
        regex.lastIndex = 0;
        match = regex.exec(text);
    }
    
    if (match) {
      const start = match.index;
      const end = start + searchText.length;
      
      textarea.focus();
      textarea.setSelectionRange(start, end);

      const textBefore = text.substring(0, start);
      const lines = textBefore.split('\n').length;
      const lineHeight = 24; // Updated for new relaxed line-height (1.5rem)
      const containerHeight = textarea.clientHeight;
      
      const scrollTop = (lines * lineHeight) - (containerHeight / 2);
      
      textarea.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth'
      });
    }
  }, [searchText]);

  const handleReplace = () => {
    if (!textareaRef.current || !searchText) return;
    const textarea = textareaRef.current;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    if (selectedText.toLowerCase() === searchText.toLowerCase()) {
      const newValue = textarea.value.substring(0, start) + replaceText + textarea.value.substring(end);
      setInput(newValue);
      setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start + replaceText.length);
      }, 0);
    } else {
        findNext();
    }
  };

  const handleReplaceAll = () => {
    if (!searchText) return;
    const escapedTerm = escapeRegExp(searchText);
    const regex = new RegExp(escapedTerm, 'gi');
    const newValue = input.replace(regex, replaceText);
    setInput(newValue);
  };

  // --- Theme Classes ---
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-100',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    editorBg: darkMode ? 'bg-[#1e1e1e]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-[#858585]' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-700',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    inputBorder: darkMode ? 'border-[#3e3e42]' : 'border-gray-300',
    hover: darkMode ? 'hover:bg-[#2a2d2e]' : 'hover:bg-gray-100',
    activeNav: darkMode ? 'bg-[#37373d] text-white' : 'bg-blue-100 text-blue-700',
    inactiveNav: darkMode ? 'text-[#cccccc] hover:bg-[#2a2d2e]' : 'text-gray-600 hover:bg-gray-100',
    button: darkMode ? 'bg-[#0e639c] hover:bg-[#1177bb]' : 'bg-blue-600 hover:bg-blue-700',
    adBg: darkMode ? 'bg-[#333333] text-[#858585]' : 'bg-gray-200 text-gray-500',
    searchToolbar: darkMode ? 'bg-[#252526] border-[#3e3e42]' : 'bg-gray-50 border-gray-200',
  };

  return (
    <div className={`flex h-screen w-full font-sans transition-colors duration-200 ${theme.bg} ${theme.text}`}>
      
      {/* --- Sidebar --- */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)} // Close when clicking outside
        />
      )}

      {/* --- Sidebar --- */}
      <aside
        className={`
          ${theme.panelBg} border-r ${theme.border} flex flex-col transition-all duration-300 
          /* Mobile: Fixed position, z-index high */
          fixed md:relative z-30 h-full
          /* Width logic */
          ${sidebarOpen ? 'w-64' : 'w-0'} 
          overflow-hidden
        `}
      >
        <div className={`flex items-center gap-3 h-16 border-b ${theme.border} px-4 flex-shrink-0`}>
          <JsonFormatterLogoSvg className="w-8 h-8" />
          {/* Hide text immediately if width is 0 to prevent text wrap ugliness */}
          <span className={`text-xl font-semibold ${theme.text} whitespace-nowrap ${!sidebarOpen && 'hidden'}`}>
            JSON Tools
          </span>
        </div>
        <nav className="p-4 space-y-2">
          <Link 
            to="/formatter" 
            onClick={() => handleNavigate('/formatter')}
            className={`flex items-center px-3 py-2 rounded-lg font-medium cursor-pointer ${view === 'formatter' ? theme.activeNav : theme.inactiveNav}`}
          >
            Formatter
          </Link>
          <Link 
            to="/minifier"
            onClick={() => handleNavigate('/minifier')}
            className={`flex items-center px-3 py-2 rounded-lg font-medium cursor-pointer ${view === 'minifier' ? theme.activeNav : theme.inactiveNav}`}
          >
            Minifier
          </Link>
        </nav>
        
        {/* --- AD: Sidebar Vertical --- */}
        <div className={`p-4 ${!sidebarOpen ? 'hidden' : ''}`}>
            <GoogleAd 
                client={ADSENSE_CLIENT_ID}
                slot={ADSENSE_SLOT_SIDEBAR}
                format="vertical"
                className={`flex items-center justify-center w-full min-h-[300px] rounded-lg overflow-hidden ${theme.adBg}`}
            />
        </div>
      </aside>

      {/* --- Main Content --- */}
      <div className="flex-1 flex flex-col overflow-hidden">
        
        {/* --- Header --- */}
        <header className={`${theme.panelBg} border-b ${theme.border} flex-shrink-0 z-10`}>
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-1 rounded-md ${theme.inactiveNav}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className={`text-lg font-semibold ${theme.text}`}>
                {view === 'formatter' ? 'JSON Formatter and Viewer' : 'JSON Minifier and Compressor'}
              </h1>
            </div>

            <div className="flex items-center gap-2">
                <button 
                  onClick={() => setDarkMode(!darkMode)}
                  className={`p-2 rounded-lg transition-colors ${theme.hover}`}
                  title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                    {darkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
                </button>
            </div>
          </div>
        </header>

        {/* --- Scrollable Content Area --- */}
        <main className="flex-1 overflow-auto p-6">
          <div className="flex flex-col gap-6">
            
            {/* --- Editor Row --- */}
            <div className="flex flex-col md:flex-row gap-6 min-h-[600px] md:h-[calc(100vh-100px)]">
              
              {/* --- Input Panel --- */}
              <div className="flex-1 flex flex-col relative">
                <div className="flex justify-between items-center mb-2 h-8 gap-2">
                    <label className={`text-sm font-medium ${theme.label} flex-shrink-0`}>Input JSON</label>
                    
                    <div className="flex items-center gap-2">
                      <input 
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".json,.txt"
                        className="hidden"
                      />
                      
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium border ${darkMode ? 'border-[#3c3c3c] text-[#cccccc] hover:bg-[#3c3c3c]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                        title="Upload JSON File"
                      >
                          <UploadIcon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Upload</span>
                      </button>

                      <button 
                        onClick={handleClear}
                        className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium border ${darkMode ? 'border-[#3c3c3c] text-[#cccccc] hover:bg-[#3c3c3c]' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                        title="Clear Input"
                      >
                          <TrashIcon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Clear</span>
                      </button>

                      <div className={`h-4 w-px mx-1 ${darkMode ? 'bg-[#3c3c3c]' : 'bg-gray-300'}`}></div>

                      <button 
                        onClick={() => setShowSearch(!showSearch)}
                        className={`p-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium ${showSearch ? theme.activeNav : theme.inactiveNav}`}
                        title="Find & Replace (Ctrl+F)"
                      >
                          <SearchIcon className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">{showSearch ? 'Hide' : 'Search'}</span>
                      </button>
                    </div>
                </div>

                {/* --- Search & Replace Toolbar --- */}
                {showSearch && (
                  <div className={`mb-2 p-2 border rounded-lg animate-in slide-in-from-top-2 duration-200 ${theme.searchToolbar}`}>
                      <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                  <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                  <input 
                                      id="search-input"
                                      type="text"
                                      placeholder="Find..." 
                                      className={`w-full pl-8 pr-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc]' : 'bg-white border-gray-300 text-gray-900'}`}
                                      value={searchText}
                                      onChange={(e) => setSearchText(e.target.value)}
                                      onKeyDown={(e) => { 
                                        if(e.key === 'Enter') {
                                          e.preventDefault(); 
                                          findNext();
                                        }
                                      }}
                                  />
                              </div>
                              <button 
                                  onClick={findNext} 
                                  className={`px-2 py-1 text-xs font-medium border rounded ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                  Next
                              </button>
                              <button 
                                  onClick={() => setShowReplace(!showReplace)}
                                  className={`p-1 rounded ${showReplace ? (darkMode ? 'bg-[#3c3c3c] text-white' : 'bg-blue-100 text-blue-700') : (darkMode ? 'text-[#cccccc] hover:bg-[#3c3c3c]' : 'text-gray-600 hover:bg-gray-200')}`}
                              >
                                  <ReplaceIcon className="w-4 h-4" />
                              </button>
                              <button 
                                onClick={() => { setShowSearch(false); setSearchText(''); }}
                                className={`p-1 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                              >
                                  <XIcon className="w-4 h-4" />
                              </button>
                          </div>

                          {showReplace && (
                              <div className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-bold">R</span>
                                      <input 
                                          type="text"
                                          placeholder="Replace..." 
                                          className={`w-full pl-8 pr-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc]' : 'bg-white border-gray-300 text-gray-900'}`}
                                          value={replaceText}
                                          onChange={(e) => setReplaceText(e.target.value)}
                                      />
                                  </div>
                                  <button 
                                      onClick={handleReplace}
                                      className={`px-2 py-1 text-xs font-medium text-white rounded ${theme.button}`}
                                  >
                                      Replace
                                  </button>
                                  <button 
                                      onClick={handleReplaceAll}
                                      className={`px-2 py-1 text-xs font-medium border rounded ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                  >
                                      All
                                  </button>
                              </div>
                          )}
                      </div>
                  </div>
                )}
                
                {/* --- REPLACED: Raw Textarea with LineNumberEditor --- */}
                <LineNumberEditor 
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  theme={theme}
                  placeholder="Paste your JSON here..."
                  className="shadow-sm"
                />
                
                <div className={`flex items-center justify-between h-12 px-3 py-2 mt-2 border rounded-lg shadow-sm ${theme.panelBg} ${theme.border}`}>
                  <div className="flex items-center gap-2">
                    {error && (
                      <span className="flex items-center gap-2 text-sm font-medium text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                        {error}
                      </span>
                    )}
                    {!error && jsonData && (
                      <span className="flex items-center gap-2 text-sm font-medium text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        Valid JSON
                      </span>
                    )}
                    {!error && !jsonData && (
                      <span className={`text-sm font-medium ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        Waiting for input...
                      </span>
                    )}
                  </div>
                  <button
                    disabled={!error}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed ${theme.button}`}
                  >
                    <Zap className="w-4 h-4" />
                    AI Fix (Soon)
                  </button>
                </div>
              </div>

              {/* --- Output Panel --- */}
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between items-center mb-2 h-8 gap-2">
                  <label className={`text-sm font-medium ${theme.label}`}>
                    {view === 'formatter' ? 'Formatted View' : 'Minified Output'}
                  </label>
                  
                  <div className="flex items-center gap-2">
                     {/* --- Expand/Collapse Buttons (Formatted View Only) --- */}
                    {view === 'formatter' && jsonData && (
                      <>
                        <button
                          onClick={handleExpandAll}
                          className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-colors ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          title="Expand All"
                        >
                          <ExpandAllIcon className="w-3 h-3" />
                          {/* <span className="hidden sm:inline">Expand</span> */}
                        </button>
                        <button
                          onClick={handleCollapseAll}
                          className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-colors ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          title="Collapse All (Default)"
                        >
                          <CollapseAllIcon className="w-3 h-3" />
                          {/* <span className="hidden sm:inline">Collapse</span> */}
                        </button>
                        <div className={`h-4 w-px mx-1 ${darkMode ? 'bg-[#3c3c3c]' : 'bg-gray-300'}`}></div>
                      </>
                    )}

                    {jsonData && (
                      <button
                        onClick={handleDownload}
                        className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-colors ${darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        title="Download Result"
                      >
                        <DownloadIcon className="w-3 h-3" />
                        <span className="hidden sm:inline">Download</span>
                      </button>
                    )}

                    {view === 'minifier' && jsonData && (
                      <button
                        onClick={handleCopyMinified}
                        className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-colors ${
                          copied 
                          ? 'bg-green-100 text-green-700 border-green-200' 
                          : (darkMode ? 'bg-[#3c3c3c] border-[#3c3c3c] text-[#cccccc] hover:bg-[#4c4c4c]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                        }`}
                      >
                        {copied ? <CheckCircle className="w-3 h-3" /> : <CopyIcon className="w-3 h-3" />}
                        <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* --- Output Content --- */}
                {/* NOTE: 
                    If View == Minifier, we use LineNumberEditor.
                    If View == Formatter, we use the Tree View. Tree View cannot easily have line numbers 
                    because lines are dynamic based on expanded/collapsed state.
                */}
                  
                  {/* --- Condition 1: Minifier View --- */}
                  {view === 'minifier' ? (
                    jsonData ? (
                       // REPLACED: Minified Textarea with LineNumberEditor
                      <LineNumberEditor
                        value={minifiedJson}
                        onChange={() => {}} // Read only
                        readOnly={true}
                        theme={theme}
                        className="h-full shadow-sm"
                      />
                    ) : (
                      <div className={`flex-1 w-full h-full p-4 border rounded-lg shadow-sm overflow-auto flex flex-col ${theme.editorBg} ${theme.border} text-sm text-gray-500`}>
                         {error ? "Fix the invalid JSON to see the minified view." : "Minified JSON will appear here..."}
                      </div>
                    )
                  ) : (
                    /* --- Condition 2: Formatter View (Default) --- */
                     <div className={`flex-1 w-full p-4 border rounded-lg shadow-sm overflow-auto relative ${theme.editorBg} ${theme.border}`}>
                      {jsonData && (
                        typeof jsonData === 'object' && jsonData !== null ? (
                          <div className="pl-1"> 
                             {/* Added padding left to mimic gutter feel slightly */}
                            <JsonNode 
                              nodeKey="(root)" 
                              value={jsonData} 
                              level={0} 
                              searchTerm={searchText}
                              darkMode={darkMode}
                              expandTrigger={expandTrigger}
                            />
                          </div>
                        ) : (
                          <div className="font-mono text-sm">
                            <span className={`
                              ${typeof jsonData === 'string' ? (darkMode ? 'text-[#ce9178]' : 'text-red-700') : ''}
                              ${typeof jsonData === 'boolean' ? (darkMode ? 'text-[#569cd6]' : 'text-blue-700') : ''}
                              ${typeof jsonData === 'number' ? (darkMode ? 'text-[#b5cea8]' : 'text-green-700') : ''}
                              ${jsonData === null ? (darkMode ? 'text-[#569cd6]' : 'text-gray-500') : ''}
                            `}>
                                {typeof jsonData === 'string' ? '"' : ''}
                                <HighlightText text={String(jsonData)} highlight={searchText} />
                                {typeof jsonData === 'string' ? '"' : ''}
                            </span>
                          </div>
                        )
                      )}
                      {error && (
                        <div className="text-sm text-gray-500">
                          Fix the invalid JSON to see the formatted view.
                        </div>
                      )}
                      {!jsonData && !error && (
                        <div className="text-sm text-gray-500">
                          Formatted JSON will appear here...
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>

            {/* --- SEO Content --- */}
             <article className={`max-w-4xl mx-auto prose ${darkMode ? 'prose-invert' : ''} ${theme.mutedText} text-sm mt-10 mb-12`}>
              {/* --- SEO: Dynamic H1 & Intro --- */}
              <div className="mb-8 border-b pb-8 border-gray-200 dark:border-gray-700">
                <h2 className={`text-2xl font-bold mb-4 ${theme.text}`}>
                  {view === 'formatter' ? 'Best Free Online JSON Formatter, Validator, Beautifier & Editor' : 'Best Free Online JSON Minifier, Compressor & Optimizer'}
                </h2>
                <p className="mb-4 text-base leading-7">
                  {view === 'formatter'
                    ? <>JSON Formatter Pro is the most powerful and secure <strong>online JSON formatter</strong> available. This free <strong>JSON validator</strong> and <strong>JSON beautifier</strong> helps developers format, validate, prettify, and debug JSON data instantly. Whether you're working with REST APIs, configuration files, web services, or complex data structures, our <strong>JSON formatter tool</strong> provides real-time syntax validation, interactive tree view, and professional formatting—all processed 100% in your browser. Unlike other <strong>JSON formatters</strong>, we never send your sensitive data to our servers, making this the safest <strong>online JSON editor</strong> for production use.</>
                    : <>Optimize your JSON for production with our powerful <strong>JSON minifier</strong> and <strong>JSON compressor</strong>. This free <strong>JSON minify tool</strong> removes unnecessary whitespace, newlines, and indentation to reduce file size by up to 70%, improving API response times and lowering bandwidth costs. Our <strong>JSON minifier</strong> validates syntax during compression, ensuring your minified JSON remains error-free and ready for production deployment.</>}
                </p>
                <p className="mb-4 text-base leading-7">
                  As a <strong>client-side JSON formatter</strong>, all processing happens locally using native browser APIs. Your JSON data, API keys, configuration files, and sensitive information never leave your device. This makes JSON Formatter Pro the most secure <strong>online JSON validator</strong> and <strong>JSON prettifier</strong> available—perfect for developers who prioritize privacy and security.
                </p>
              </div>

              {/* What is JSON Section */}
              <div className={`mb-8 border-b pb-8 border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>What is JSON and Why Use a JSON Formatter?</h3>
                <p className="mb-4 text-base leading-7">
                  <strong>JSON</strong> (JavaScript Object Notation) is a lightweight data-interchange format that's become the standard for modern web APIs, configuration files, and data storage. It's human-readable, language-independent, and easy to parse. However, JSON data from APIs or files is often minified (compressed) or poorly formatted, making it difficult to read and debug.
                </p>
                <p className="mb-4 text-base leading-7">
                  A <strong>JSON formatter</strong> (also called a <strong>JSON beautifier</strong> or <strong>JSON prettifier</strong>) takes messy, unformatted JSON and converts it into properly indented, readable code. A <strong>JSON validator</strong> checks your JSON syntax for errors like missing commas, trailing commas, unclosed brackets, or invalid characters. Our <strong>JSON formatter online</strong> combines both capabilities—formatting and validation—in one powerful, secure tool.
                </p>
                <p className="mb-4 text-base leading-7">
                  Whether you need to <strong>format JSON</strong>, <strong>validate JSON</strong>, <strong>beautify JSON</strong>, <strong>prettify JSON</strong>, or <strong>minify JSON</strong>, JSON Formatter Pro provides all the tools you need in one place, with enterprise-grade security and privacy.
                </p>
              </div>

              {/* Use Cases Section */}
              <div className={`mb-8 border-b pb-8 border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>Common Use Cases for JSON Formatter</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>API Development & Debugging</h4>
                    <p className="text-sm leading-6">
                      Format and validate JSON responses from REST APIs, GraphQL queries, or webhooks. Our <strong>JSON validator</strong> helps you quickly identify syntax errors in API responses, while the <strong>JSON formatter</strong> makes complex nested structures readable.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Configuration File Management</h4>
                    <p className="text-sm leading-6">
                      Format and validate <code>package.json</code>, <code>tsconfig.json</code>, <code>.eslintrc</code>, and other configuration files. Ensure your config files are properly formatted and error-free before deployment.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Data Analysis & Visualization</h4>
                    <p className="text-sm leading-6">
                      Use our interactive <strong>JSON tree view</strong> to explore large datasets, navigate nested objects, and understand complex data structures. Collapse and expand nodes to focus on specific data sections.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Production Optimization</h4>
                    <p className="text-sm leading-6">
                      Minify JSON payloads to reduce file size and improve API performance. Our <strong>JSON minifier</strong> removes unnecessary whitespace while maintaining data integrity, perfect for production deployments.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Code Review & Documentation</h4>
                    <p className="text-sm leading-6">
                      Format JSON examples in documentation, code reviews, or technical specifications. A well-formatted <strong>JSON example</strong> is easier to understand and maintain than minified code.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Error Debugging</h4>
                    <p className="text-sm leading-6">
                      Quickly identify JSON syntax errors with our real-time <strong>JSON validator</strong>. Error messages point to exact line numbers and character positions, making debugging fast and efficient.
                    </p>
                  </div>
                </div>
              </div>

              {/* --- SEO: Key Features Grid --- */}
              <div className="grid md:grid-cols-2 gap-8 mb-10">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>Why Choose This JSON Formatter Over Others?</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li><strong>100% Client-Side Processing:</strong> Unlike other tools, your JSON data is processed entirely in your browser using native <code>JSON.parse()</code> and <code>JSON.stringify()</code> APIs. Your sensitive data never leaves your device—perfect for API keys, tokens, and production data.</li>
                    <li><strong>Real-Time Syntax Validation:</strong> Our <strong>JSON validator</strong> detects and highlights errors like trailing commas, missing quotes, unclosed brackets, or invalid characters instantly as you type.</li>
                    <li><strong>Interactive Tree View:</strong> Navigate large JSON structures with our expandable/collapsible tree view. Perfect for exploring complex nested objects and arrays.</li>
                    <li><strong>File Support:</strong> Upload <code>.json</code> or <code>.txt</code> files directly, or download your formatted/minified results with one click. No file size limits.</li>
                    <li><strong>Auto-Save Functionality:</strong> Your work is automatically saved to browser local storage, so you never lose progress even if you close the tab.</li>
                    <li><strong>No Registration Required:</strong> Start formatting and validating JSON immediately—no account creation, no email verification, no usage limits.</li>
                  </ul>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${theme.text}`}>Advanced JSON Editor Features</h3>
                  <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li><strong>VS Code-Like Editor:</strong> Professional code editor with line numbers, syntax highlighting, and familiar keyboard shortcuts. Press <code>Ctrl+F</code> (or <code>Cmd+F</code> on Mac) for search and replace.</li>
                    <li><strong>Search & Replace:</strong> Find and replace text within your JSON using regex support. Navigate through multiple matches quickly.</li>
                    <li><strong>Dark Mode & Light Mode:</strong> Comfortable viewing in both dark and light themes. Your preference is automatically saved.</li>
                    <li><strong>Copy to Clipboard:</strong> One-click copying of formatted or minified JSON. Perfect for pasting into code editors or documentation.</li>
                    <li><strong>Error Highlighting:</strong> Invalid JSON is highlighted with clear error messages showing exact line numbers and error types.</li>
                    <li><strong>Format Preservation:</strong> Maintains data structure integrity while formatting. No data loss, no corruption, just clean, readable JSON.</li>
                  </ul>
                </div>
              </div>

              {/* How to Use Section */}
              <div className={`mb-8 border-b pb-8 border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>How to Use This JSON Formatter</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Step 1: Input Your JSON</h4>
                    <p className="text-sm leading-6">
                      Paste your JSON data directly into the editor, or click the "Upload" button to load a <code>.json</code> or <code>.txt</code> file. 
                      You can paste minified JSON, unformatted JSON, or even JSON with syntax errors—our <strong>JSON formatter</strong> will handle it.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>Step 2: Automatic Validation & Formatting</h4>
                    <p className="text-sm leading-6">
                      The <strong>JSON validator</strong> automatically checks your syntax in real-time. If valid, the formatted JSON appears in the output panel with proper indentation and structure. 
                      If invalid, error messages guide you to the exact problem location.
                    </p>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>
                      {view === 'formatter' ? 'Step 3: Explore with Tree View' : 'Step 3: Copy Minified JSON'}
                    </h4>
                    <p className="text-sm leading-6">
                      {view === 'formatter' 
                        ? 'Use the interactive tree view to navigate large JSON structures. Collapse and expand nodes to focus on specific data sections. Click "Download" to save the formatted JSON, or copy it to your clipboard.'
                        : 'Copy the minified JSON output to your clipboard with one click, or download it as a file. The minified JSON is optimized for production use with reduced file size and faster parsing.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* JSON vs Other Formats */}
              <div className={`mb-8 border-b pb-8 border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>JSON vs XML vs YAML: Why JSON is the Standard</h3>
                <div className="grid md:grid-cols-3 gap-6 mb-4">
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>JSON Advantages</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Lightweight and compact</li>
                      <li>Native browser support</li>
                      <li>Easy to parse and generate</li>
                      <li>Human-readable format</li>
                      <li>Standard for REST APIs</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>XML Characteristics</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Verbose and larger file size</li>
                      <li>More complex parsing</li>
                      <li>Legacy enterprise systems</li>
                      <li>Strict schema validation</li>
                      <li>Namespace support</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-2 ${theme.text}`}>YAML Use Cases</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm">
                      <li>Configuration files</li>
                      <li>Docker Compose</li>
                      <li>CI/CD pipelines</li>
                      <li>More readable than JSON</li>
                      <li>Supports comments</li>
                    </ul>
                  </div>
                </div>
                <p className="text-sm leading-6">
                  JSON has become the de facto standard for web APIs because it's lightweight, easy to work with, and natively supported by JavaScript. 
                  Our <strong>JSON formatter</strong> makes working with JSON even easier by providing instant formatting, validation, and beautification.
                </p>
              </div>

              {/* Best Practices Section */}
              <div className={`mb-8 border-b pb-8 border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>JSON Formatting Best Practices</h3>
                <ul className="list-disc pl-5 space-y-2 text-sm">
                  <li><strong>Always Validate Before Use:</strong> Use our <strong>JSON validator</strong> to check syntax before deploying to production. Invalid JSON can break applications.</li>
                  <li><strong>Format for Readability:</strong> Use our <strong>JSON formatter</strong> to beautify JSON in development and documentation. Well-formatted JSON is easier to debug and maintain.</li>
                  <li><strong>Minify for Production:</strong> Use our <strong>JSON minifier</strong> to reduce file size in production APIs. Smaller payloads mean faster response times and lower bandwidth costs.</li>
                  <li><strong>Handle Errors Gracefully:</strong> Always validate JSON from external sources. Our <strong>JSON validator</strong> helps identify issues before they cause runtime errors.</li>
                  <li><strong>Use Consistent Formatting:</strong> Format all JSON files in your project consistently. Our <strong>JSON formatter</strong> ensures uniform indentation and structure.</li>
                  <li><strong>Protect Sensitive Data:</strong> Never paste API keys, tokens, or passwords into online tools that send data to servers. Our <strong>client-side JSON formatter</strong> keeps your data secure.</li>
                </ul>
              </div>

              {/* --- SEO: FAQ Section (Rich Snippet Bait) --- */}
              <h3 className={`text-xl font-semibold mt-8 mb-6 ${theme.text}`}>Frequently Asked Questions About JSON Formatter</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Is this JSON formatter free to use?</h4>
                  <p className="text-sm leading-6">
                    Yes! JSON Formatter Pro is completely free with no registration required, no usage limits, and no hidden fees. 
                    As a <strong>client-side JSON formatter</strong>, all processing happens in your browser, so we don't need to charge for server resources.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>How do I validate JSON online?</h4>
                  <p className="text-sm leading-6">
                    Simply paste your JSON code into the editor or use the "Upload" button to load a file. Our <strong>JSON validator</strong> automatically checks syntax in real-time. 
                    If your JSON is invalid, the tool displays an error message pointing to the exact line number and character position. Once fixed, the "Valid JSON" indicator appears.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Is this JSON formatter secure and private?</h4>
                  <p className="text-sm leading-6">
                    Yes, absolutely. Unlike many other <strong>online JSON formatters</strong>, <strong>we do not store or send your JSON data to our servers</strong>. 
                    All parsing, formatting, and validation occur strictly within your web browser using native <code>JSON.parse()</code> and <code>JSON.stringify()</code> APIs. 
                    This makes it safe for formatting sensitive configuration files, API responses with tokens, or production data. 
                    Note that third-party services like Google AdSense may collect standard usage data for advertising purposes, as disclosed in our <a href="/privacy" className="underline">Privacy Policy</a>.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>What's the difference between format, beautify, and prettify JSON?</h4>
                  <p className="text-sm leading-6">
                    These terms are often used interchangeably. <strong>Format JSON</strong>, <strong>beautify JSON</strong>, and <strong>prettify JSON</strong> all mean the same thing: 
                    converting unformatted or minified JSON into properly indented, readable code. Our <strong>JSON formatter</strong> does all three—taking messy JSON and making it beautiful and readable.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Why should I minify JSON?</h4>
                  <p className="text-sm leading-6">
                    Minification removes unnecessary characters (spaces, newlines, tabs) from JSON without changing its functionality. 
                    This significantly reduces payload size—often by 50-70%—making API transfers faster, reducing bandwidth costs, and improving application performance. 
                    Our <strong>JSON minifier</strong> validates syntax during compression, ensuring your minified JSON remains error-free.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Can I format JSON with errors?</h4>
                  <p className="text-sm leading-6">
                    Our <strong>JSON validator</strong> will detect syntax errors and highlight them, but you cannot format invalid JSON. 
                    You must fix errors like missing commas, unclosed brackets, or trailing commas before formatting. 
                    The error messages guide you to the exact problem location, making fixes quick and easy.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>What is the difference between JSON and XML?</h4>
                  <p className="text-sm leading-6">
                    JSON (JavaScript Object Notation) is lighter, easier to read, and faster to parse than XML. 
                    JSON is the standard format for modern REST APIs and web services, while XML is more verbose and often used in legacy enterprise systems. 
                    JSON uses key-value pairs and arrays, while XML uses tags and attributes. Our <strong>JSON formatter</strong> works exclusively with JSON format.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Does this JSON formatter support large files?</h4>
                  <p className="text-sm leading-6">
                    Yes! Since all processing happens in your browser, file size is limited only by your browser's memory. 
                    We've successfully formatted JSON files with thousands of lines. For extremely large files (100MB+), consider splitting them into smaller chunks.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Can I use this JSON formatter offline?</h4>
                  <p className="text-sm leading-6">
                    After the initial page load, yes! All the formatting and validation logic runs entirely in your browser using native JavaScript APIs. 
                    Your browser caches the necessary code, allowing you to format and validate JSON even without an internet connection.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>How is this different from other online jsonformatter tools?</h4>
                  <p className="text-sm leading-6">
                    Unlike similar tools, JSON Formatter Pro processes all data <strong>100% in your browser</strong>—your JSON never leaves your device. 
                    We also offer an interactive tree view, VS Code-like editor with search & replace, auto-save functionality, and better error handling. 
                    Most importantly, we prioritize your privacy and security above all else.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Does this JSON validator check for data types?</h4>
                  <p className="text-sm leading-6">
                    Our <strong>JSON validator</strong> checks for syntax correctness (valid JSON structure), not data type validation or schema validation. 
                    It ensures your JSON is properly formatted with correct brackets, commas, and quotes. For schema validation, you would need additional tools like JSON Schema validators.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>Can I format JSON arrays and nested objects?</h4>
                  <p className="text-sm leading-6">
                    Absolutely! Our <strong>JSON formatter</strong> handles all JSON data types including arrays, nested objects, strings, numbers, booleans, and null values. 
                    The interactive tree view makes it especially easy to navigate and explore complex nested structures.
                  </p>
                </div>
                <div>
                  <h4 className={`font-medium mb-2 ${theme.text}`}>What keyboard shortcuts are available?</h4>
                  <p className="text-sm leading-6">
                    Press <code>Ctrl+F</code> (or <code>Cmd+F</code> on Mac) to open the search and replace dialog. 
                    Use standard text editor shortcuts like <code>Ctrl+A</code> to select all, <code>Ctrl+C</code> to copy, and <code>Ctrl+V</code> to paste.
                  </p>
                </div>
              </div>

              {/* Related Tools Section */}
              <div className={`mt-8 pt-6 border-t border-gray-200 dark:border-gray-700`}>
                <h3 className={`text-lg font-semibold mb-4 ${theme.text}`}>Related Developer Tools</h3>
                <p className="text-sm leading-6 mb-4">
                  If you're working with Protocol Buffers, check out our <a href="https://protobuf.echologai.com" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">Proto Workbench</a> for Protobuf compilation and decoding. 
                  For more developer tools from the EchoLog ecosystem, visit <a href="https://echologai.com" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">Echolog AI</a>.
                </p>
              </div>
            </article>

            {/* --- Footer & AD: Horizontal --- */}
            <footer className="flex-shrink-0 pt-0 flex flex-col items-center gap-4">
              <GoogleAd 
                client={ADSENSE_CLIENT_ID}
                slot={ADSENSE_SLOT_FOOTER}
                format="horizontal"
                className={`flex items-center justify-center w-full min-h-[90px] rounded-lg overflow-hidden ${theme.adBg}`}
              />
              <Footer darkMode={darkMode} />
            </footer>
            
          </div>
        </main>
      </div>
    </div>
  );
}

// --- Main App with Routing ---
export default function App() {
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('jsonfmt_dark_mode');
      return saved !== null ? saved === 'true' : true;
    }
    return true;
  });

  useEffect(() => {
    localStorage.setItem('jsonfmt_dark_mode', String(darkMode));
  }, [darkMode]);

  return (
    <>
      <CookieConsent darkMode={darkMode} />
      <Routes>
        <Route path="/" element={<JsonFormatterView darkMode={darkMode} setDarkMode={setDarkMode} />} />
        <Route path="/formatter" element={<JsonFormatterView darkMode={darkMode} setDarkMode={setDarkMode} />} />
        <Route path="/minifier" element={<JsonFormatterView darkMode={darkMode} setDarkMode={setDarkMode} />} />
        <Route path="/privacy" element={<PrivacyPage darkMode={darkMode} />} />
        <Route path="/terms" element={<TermsPage darkMode={darkMode} />} />
        <Route path="/about" element={<AboutPage darkMode={darkMode} />} />
      </Routes>
    </>
  );
}